import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import React from 'react';

interface TeamDeleteTarget {
    id: number;
    name: string;
}

interface DeleteTeamDialogProps {
    open: boolean;
    onClose(): void;
    onSuccess(): void;
    team: TeamDeleteTarget | null;
}

export const DeleteTeamDialog: React.FC<DeleteTeamDialogProps> = ({
    open,
    onClose,
    onSuccess,
    team
}) => {
    const { challengeClient } = injectTSDI(Api);
    const [error, setError] = React.useState<string | null>(null);

    const deleteTeam = useWrapRequest<void, [number]>(
        async (teamId) => {
            await challengeClient.support.teamSupportControllerDeleteTeam(
                teamId
            );
        },
        {
            successMessage: () => 'Team deleted'
        }
    );

    const handleClose = () => {
        if (deleteTeam.loading) {
            return;
        }

        setError(null);
        onClose();
    };

    const handleDelete = async () => {
        if (!team) {
            return;
        }

        setError(null);

        try {
            await deleteTeam.request([team.id], { throwError: true });
            onSuccess();
            handleClose();
        } catch {
            setError('Failed to delete team. Please try again.');
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    handleClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Delete Team</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete the team &quot;
                        {team?.name}
                        &quot;? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={deleteTeam.loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteTeam.loading}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
